import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Chip, Field, Page, Tag, TextInput, Thumb } from '../components/basics.tsx';
import { CouldNotLoad, Empty, Loading } from '../components/states.tsx';
import { api, query } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';

/**
 * Screen 14: one box over the six things people name out loud — a take, a
 * camera, a pass, a movie, an edit, a note — plus the board filters somebody
 * saved.
 *
 * A saved filter's match count is read with the query the board will run, not
 * remembered from when it was saved, so it cannot quietly promise rows that are
 * no longer there.
 */

interface Hit {
  type: string;
  id: string;
  name: string;
  detail: string;
  where: string | null;
  thumbnail: string | null;
  href: string;
}

interface Results {
  hits: Hit[];
  counts: Record<string, number>;
  total: number;
}

interface SavedFilter {
  _id: string;
  name: string;
  owner: string;
  shared?: boolean;
  conditions: Record<string, unknown>;
  matches: number | null;
  why_no_count: string | null;
}

const TYPE_WORDS: Record<string, string> = {
  take: 'Takes',
  vc: 'Virtual cameras',
  pass: 'VC passes',
  movie: 'Movies',
  edit: 'Edits',
  note: 'Notes'
};

export function Search({
  projectId,
  onOpenBoard
}: {
  projectId: string;
  onOpenBoard: (conditions: Record<string, unknown>) => void;
}) {
  const [q, setQ] = useState('');
  const [type, setType] = useState<string | null>(null);

  const { state, reload } = useLoad<Results>(
    () => api.get(`/api/search${query({ project_id: projectId, q, type: type ?? undefined })}`),
    [projectId, q, type]
  );
  const { state: saved, reload: reloadSaved } = useLoad<{ saved_filters: SavedFilter[] }>(
    () => api.get(`/api/saved-filters?project_id=${encodeURIComponent(projectId)}`),
    [projectId]
  );

  const results = state.data;

  const remove = async (id: string): Promise<void> => {
    await api.del(`/api/saved-filters/${id}`).catch(() => undefined);
    reloadSaved();
  };

  return (
    <Page
      eyebrow="MAESTRO · SEARCH"
      title="Search"
      description="One box over takes, virtual cameras, VC passes, movies, edits and notes."
    >
      <Card>
        <Field label="What are you looking for" grow>
          <TextInput value={q} onChange={setQ} placeholder="cam0003, sc01, reel_01, a phrase from a note…" />
        </Field>
        {results && q !== '' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight }}>
            {Object.entries(results.counts)
              .filter(([, n]) => n > 0)
              .map(([key, n]) => (
                <Chip key={key} on={type === key} onClick={() => setType(type === key ? null : key)}>
                  {TYPE_WORDS[key] ?? key} {n}
                </Chip>
              ))}
          </div>
        )}
      </Card>

      {state.status === 'loading' && q !== '' && !results && <Loading says="Searching…" rows={4} />}
      {state.status === 'failed' && (
        <CouldNotLoad what="the search" error={state.error} onRetry={reload} />
      )}
      {results && q !== '' && results.total === 0 && (
        <Empty title="Nothing matches that" says="Try a shorter word, or part of a camera number." />
      )}

      {(results?.hits ?? []).map((hit) => (
        <Card key={`${hit.type}-${hit.id}`}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}>
            <Thumb url={hit.thumbnail} alt={hit.name} size={72} />
            <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
                <span style={text.bodyStrong}>{hit.name}</span>
                <Tag>{TYPE_WORDS[hit.type] ?? hit.type}</Tag>
              </span>
              <span style={{ ...text.note, color: colour.inkSoft }}>{hit.detail}</span>
              {hit.where && <Tag tone="mono">{hit.where}</Tag>}
            </div>
          </div>
        </Card>
      ))}

      <Card title="Saved filters" aside="Counted now, with the query the board will run.">
        {(saved.data?.saved_filters ?? []).length === 0 ? (
          <span style={{ ...text.small, color: colour.inkSoft }}>
            None yet. Filter the board and use "Save this filter".
          </span>
        ) : (
          (saved.data?.saved_filters ?? []).map((filter) => (
            <div
              key={filter._id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: space.control,
                paddingTop: space.tight,
                borderTop: `1px solid ${colour.edgeSoft}`
              }}
            >
              <div style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={text.bodyStrong}>{filter.name}</span>
                <span style={{ ...text.note, color: colour.inkSoft }}>
                  {filter.why_no_count ??
                    `${filter.matches} ${filter.matches === 1 ? 'pass matches' : 'passes match'} now`}
                  {filter.shared ? ' · shared with the team' : ' · yours'}
                </span>
              </div>
              <Button onClick={() => onOpenBoard(filter.conditions)}>Open on the board</Button>
              <Button tone="danger" onClick={() => void remove(filter._id)}>
                Delete
              </Button>
            </div>
          ))
        )}
      </Card>
    </Page>
  );
}
